import { useMutation, useQuery } from '@tanstack/react-query';
import { Edit2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { customerAPI, insuranceAPI } from '../api/index';
import { queryClient } from '../App';
import { Button } from '../components/Button';
import { ExpiryBadge } from '../components/ExpiryBadge';
import AddInsuranceModal from '../components/modals/AddInsuranceModal';
import { PageHeader } from '../components/PageHeader';
import { daysRemaining, formatPaise } from '../lib/utils';
import type { Insurance } from '../types/index';

export function InsurancesPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<'all' | 'active' | 'expiring_soon' | 'expired'>('all')
  const [offset, setOffset] = useState(0)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [showCustomerPicker, setShowCustomerPicker] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [selectedInsurance, setSelectedInsurance] = useState<Insurance | undefined>()
  const [customerSearch, setCustomerSearch] = useState('')

  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers', 'insurance-picker'],
    queryFn: () =>
      customerAPI.list({
        limit: 200,
        offset: 0,
        active: true,
      }).then((r) => r.data),
  })

  const customers = customersData?.customers || []
  const filteredCustomers = customers.filter((customer) =>
    `${customer.full_name} ${customer.phone}`.toLowerCase().includes(customerSearch.toLowerCase())
  )

  const { data, isLoading } = useQuery({
    queryKey: ['insurances', status, offset],
    queryFn: () =>
      insuranceAPI.list({
        status: status === 'all' ? undefined : status,
        limit: 20,
        offset,
      }).then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => insuranceAPI.delete(id),
    onSuccess: () => {
      toast.success('Insurance deleted')
      queryClient.invalidateQueries({ queryKey: ['insurances'] })
      setDeleteId(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to delete')
    },
  })

  const total = data?.total || 0
  const insurances = data?.insurances || []
  const totalPages = Math.ceil(total / 20)
  const currentPage = Math.floor(offset / 20) + 1

  const tabs = [
    { id: 'all' as const, label: 'All' },
    { id: 'active' as const, label: 'Active' },
    { id: 'expiring_soon' as const, label: 'Expiring Soon' },
    { id: 'expired' as const, label: 'Expired' },
  ]

  const handleAddInsurance = () => {
    setShowCustomerPicker(true)
  }

  const handleEditInsurance = (insurance: Insurance) => {
    setSelectedInsurance(insurance)
    setSelectedCustomerId(insurance.customer_id)
    setShowAddModal(true)
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setSelectedInsurance(undefined)
    setSelectedCustomerId('')
  }

  return (
    <>
      <PageHeader title="Insurances" action={{ label: 'Add Insurance', onClick: handleAddInsurance }} />

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setStatus(tab.id); setOffset(0) }}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              status === tab.id
                ? 'border-saffron text-saffron'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : insurances.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="mb-4">No insurances found</p>
              <Button onClick={handleAddInsurance} className="bg-saffron">
                <Plus className="w-4 h-4 mr-2" />
                Create Insurance
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4">Customer</th>
                  <th className="text-left py-3 px-4">Vehicle</th>
                  <th className="text-left py-3 px-4">Policy #</th>
                  <th className="text-left py-3 px-4">Company</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-left py-3 px-4">Premium</th>
                  <th className="text-left py-3 px-4">Expiry</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {insurances.map((insurance: any) => (
                  <tr key={insurance.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <button onClick={() => navigate(`/customers/${insurance.customer_id}`)} className="text-saffron hover:underline">
                        {insurance.customer_name}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm">{insurance.vehicle_reg}</td>
                    <td className="py-3 px-4 font-medium">{insurance.policy_number}</td>
                    <td className="py-3 px-4">{insurance.insurance_company || '-'}</td>
                    <td className="py-3 px-4"><span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">{insurance.policy_type}</span></td>
                    <td className="py-3 px-4">{insurance.premium_amount ? formatPaise(parseInt(insurance.premium_amount)) : '-'}</td>
                    <td className="py-3 px-4"><ExpiryBadge days={daysRemaining(insurance.expiry_date)} /></td>
                    <td className="py-3 px-4 text-right flex justify-end gap-2">
                      <Button size="sm" variant="secondary" onClick={() => handleEditInsurance(insurance)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setDeleteId(insurance.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && insurances.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">Showing {Math.min(offset + 20, total)} of {total}</p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 20))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setOffset(offset + 20)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm">
            <p className="text-gray-900 font-medium mb-4">Delete this insurance?</p>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => deleteMutation.mutate(deleteId)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Picker */}
      {showCustomerPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Customer</h3>
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-4 py-2 mb-3 focus:border-saffron focus:outline-none"
              disabled={customersLoading}
              autoFocus
            />
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {filteredCustomers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {customerSearch ? 'No customers found' : 'No customers available'}
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => {
                      setSelectedCustomerId(customer.id)
                      setShowCustomerPicker(false)
                      setShowAddModal(true)
                    }}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-saffron/5 transition-colors ${
                      selectedCustomerId === customer.id ? 'bg-saffron/10' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">{customer.full_name}</div>
                    <div className="text-sm text-gray-500">{customer.phone}</div>
                  </button>
                ))
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button 
                variant="secondary" 
                onClick={() => { 
                  setShowCustomerPicker(false)
                  setSelectedCustomerId('')
                  setCustomerSearch('')
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Insurance Modal */}
      <AddInsuranceModal
        open={showAddModal}
        onClose={handleCloseModal}
        customerId={selectedCustomerId}
        insurance={selectedInsurance}
        onSuccess={() => {
          handleCloseModal()
          queryClient.invalidateQueries({ queryKey: ['insurances'] })
        }}
      />
    </>
  )
}
